/**
 * Branded types for money amounts and rates.
 *
 * Backend Pydantic `Decimal` fields serialize as JSON strings to preserve
 * precision. Without a brand, JavaScript silently coerces `"0.20" * 100`
 * and hides type errors (see #719, OrgDetailPage.tsx:260).
 *
 * `Money` and `Rate` are string brands — structurally strings at runtime,
 * but the TypeScript type system treats them as distinct nominal types.
 * Callers cannot do raw arithmetic (`money * 2`) without first unwrapping
 * via the helpers in this module.
 *
 * Backend emits the string wire value. Frontend narrows at the edge via
 * `asMoney` / `asRate` (typically done automatically by the codegen
 * post-processor, not called by hand).
 *
 * Usage:
 *   import { type Money, sumMoney, unwrapMoney, formatMoney } from "@bw/shared/types";
 *
 *   const fee: Money = charge.fee_amount;     // already branded by codegen
 *   const total = sumMoney([fee, fee, fee]);  // Money (still branded)
 *   const cents = unwrapMoney(total);          // number (for Stripe boundary)
 *   const display = formatMoney(total);        // "$12.00"
 */

// --- Brand definitions --------------------------------------------------

/**
 * A dollar amount, as a Decimal string. Example wire value: "22.50".
 * Arithmetic must go through `sumMoney`, `multiplyMoney`, `subtractMoney`.
 *
 * Limitation: `money1 + money2` compiles as string concatenation (JS allows
 * string + string) but the result is typed as `string`, not Money — it can't
 * be passed to `formatMoney`/`moneyToCents`/`sumMoney` without re-branding,
 * and `asMoney("10.005.00")` throws. The ESLint rule in A2.7 bans `+` on
 * branded types to prevent this from ever reaching the type-loss.
 */
export type Money = string & { readonly __brand: "Money" };

/**
 * A rate expressed as a fraction (0..1). Example wire value: "0.20" (= 20%).
 * `commission_rate` fields use this brand.
 */
export type RateFraction = string & { readonly __brand: "RateFraction" };

/**
 * A rate expressed as a percentage (0..100). Example wire value: "8.25" (= 8.25%).
 * `fee_percentage` fields use this brand.
 */
export type RatePercent = string & { readonly __brand: "RatePercent" };

/**
 * Either rate variant. Use when the unit is determined at call site.
 */
export type Rate = RateFraction | RatePercent;

// --- Brand constructors -------------------------------------------------

/**
 * Tag a raw Decimal string as a Money value. Returns null for null/undefined;
 * throws for unparseable input (money precision is too important for silent
 * fallback). Called by codegen post-processor and at API parse edges.
 */
export function asMoney(value: string | number | null | undefined): Money | null {
  if (value === null || value === undefined) return null;
  const str = typeof value === "number" ? String(value) : value.trim();
  if (!DECIMAL_RE.test(str)) {
    throw new TypeError(`asMoney: "${value}" is not a valid Decimal string`);
  }
  return str as Money;
}

/**
 * Tag a raw Decimal string as a RateFraction (0..1).
 * Returns null for null/undefined; throws for unparseable or out-of-range input.
 */
export function asRateFraction(
  value: string | number | null | undefined,
): RateFraction | null {
  if (value === null || value === undefined) return null;
  const str = typeof value === "number" ? String(value) : value.trim();
  if (!DECIMAL_RE.test(str)) {
    throw new TypeError(`asRateFraction: "${value}" is not a valid Decimal string`);
  }
  return str as RateFraction;
}

/**
 * Tag a raw Decimal string as a RatePercent (0..100).
 */
export function asRatePercent(
  value: string | number | null | undefined,
): RatePercent | null {
  if (value === null || value === undefined) return null;
  const str = typeof value === "number" ? String(value) : value.trim();
  if (!DECIMAL_RE.test(str)) {
    throw new TypeError(`asRatePercent: "${value}" is not a valid Decimal string`);
  }
  return str as RatePercent;
}

// --- Unwrapping (escape hatches) ----------------------------------------

/**
 * Convert a Money to a JS number. Lossy beyond ~15 significant digits
 * (IEEE 754). Use for comparisons, Stripe boundary (via `moneyToCents`),
 * or places that explicitly need a number. **Never** use for arithmetic
 * that could cascade — prefer `sumMoney`, `multiplyMoney`, `subtractMoney`.
 */
export function unwrapMoney(m: Money): number {
  return Number(m);
}

/**
 * Convert a RateFraction to a JS number (0..1).
 */
export function unwrapRateFraction(r: RateFraction): number {
  return Number(r);
}

/**
 * Convert a RatePercent to a JS number (0..100).
 */
export function unwrapRatePercent(r: RatePercent): number {
  return Number(r);
}

// --- Branded arithmetic --------------------------------------------------

/**
 * Add two Money values. Uses string→number→string conversion; result is
 * quantized to 2 decimal places (absorbs IEEE 754 midpoint drift).
 */
export function sumMoney(a: Money, b: Money): Money;
export function sumMoney(list: readonly Money[]): Money;
export function sumMoney(a: Money | readonly Money[], b?: Money): Money {
  const values: readonly Money[] = Array.isArray(a) ? a : [a as Money, b as Money];
  let total = 0;
  for (const m of values) total += Number(m);
  return quantize(total) as Money;
}

/**
 * Subtract `b` from `a`. Result can be negative.
 */
export function subtractMoney(a: Money, b: Money): Money {
  return quantize(Number(a) - Number(b)) as Money;
}

/**
 * Multiply a Money amount by a scalar quantity (e.g., unit price × count).
 * Returns a Money quantized to 2 decimal places.
 */
export function multiplyMoney(m: Money, qty: number): Money {
  if (!Number.isFinite(qty)) {
    throw new TypeError(`multiplyMoney: qty must be finite, got ${qty}`);
  }
  return quantize(Number(m) * qty) as Money;
}

/**
 * Ratio of two Money values. Returns a plain `number` (not Money) because
 * the result is dimensionless. Returns null if the denominator is zero.
 */
export function ratioMoney(a: Money, b: Money): number | null {
  const denom = Number(b);
  if (denom === 0) return null;
  return Number(a) / denom;
}

/**
 * Apply a RateFraction to a Money amount. E.g. commission on a charge.
 * `applyRate(charge, commissionRate)` returns the commission in Money.
 */
export function applyRate(m: Money, rate: RateFraction): Money {
  return quantize(Number(m) * Number(rate)) as Money;
}

// --- Formatting ----------------------------------------------------------

/**
 * Format a Money for display. Null renders as em-dash. Invalid currency
 * codes fall back to em-dash (Intl throws RangeError on "").
 *
 * STRICT: non-decimal runtime garbage (`""`, `"1e3"`, `"0x14"`) renders "—",
 * never a fabricated figure — see `strictDecimalToNumber`.
 */
export function formatMoney(
  m: Money | null | undefined,
  currency = "USD",
): string {
  if (m === null || m === undefined) return "—";
  const n = strictDecimalToNumber(m);
  if (n === null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return "—";
  }
}

/**
 * Format a RateFraction as "XX%". `formatRateFraction(rate, 1)` → "22.5%".
 *
 * STRICT: non-decimal runtime garbage renders "—" (`""` would otherwise
 * fabricate "0%" — Number("") === 0 — on legal/money surfaces).
 */
export function formatRateFraction(
  r: RateFraction | null | undefined,
  fractionDigits = 0,
): string {
  if (r === null || r === undefined) return "—";
  const n = strictDecimalToNumber(r);
  if (n === null) return "—";
  return `${(n * 100).toFixed(fractionDigits)}%`;
}

/**
 * Format a RatePercent as "XX%". `formatRatePercent(rate, 2)` → "8.25%".
 *
 * STRICT: non-decimal runtime garbage renders "—".
 */
export function formatRatePercent(
  r: RatePercent | null | undefined,
  fractionDigits = 0,
): string {
  if (r === null || r === undefined) return "—";
  const n = strictDecimalToNumber(r);
  if (n === null) return "—";
  return `${n.toFixed(fractionDigits)}%`;
}

/**
 * Convert a Money amount to integer cents for Stripe. Absorbs midpoint
 * underflow via `.toFixed(2)`: `moneyToCents(asMoney("1.005"))` → 101.
 *
 * STRICT: non-decimal runtime garbage returns null (`""` would otherwise
 * become 0 cents — a fabricated amount at the payment boundary).
 */
export function moneyToCents(m: Money | null | undefined): number | null {
  if (m === null || m === undefined) return null;
  const n = strictDecimalToNumber(m);
  if (n === null) return null;
  return Math.round(Number((n * 100).toFixed(2)));
}

// --- Internals ----------------------------------------------------------

/**
 * Match plain decimal literals only. Shared with `utils/money.ts` parseDecimal.
 */
const DECIMAL_RE = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * Strict runtime gate shared by the display formatters and `moneyToCents`.
 *
 * Branded values pass DECIMAL_RE by construction (`asMoney`/`asRateFraction`/
 * `asRatePercent` enforce it), but the codegen post-processor brands TYPES
 * only — wire values reach these functions unvalidated at runtime. `Number()`
 * alone accepts `""` (→ 0), exponent (`"1e3"` → 1000), hex (`"0x14"` → 20),
 * `"Infinity"`, etc., which would FABRICATE a figure ("" → "$0.00" / "0%") on
 * legal/money surfaces. Non-decimal input returns null so callers degrade to
 * an honest "—"/null instead. Numbers (defensive: the brands are strings) are
 * gated through the same regex via String().
 */
function strictDecimalToNumber(value: string | number): number | null {
  const str = typeof value === "number" ? String(value) : value.trim();
  if (!DECIMAL_RE.test(str)) return null;
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

/**
 * Round a JS number to 2 decimal places and return as a Decimal string.
 * Absorbs IEEE 754 drift at the cent boundary.
 */
function quantize(n: number): string {
  if (!Number.isFinite(n)) {
    throw new TypeError(`quantize: value must be finite, got ${n}`);
  }
  return (Math.round(Number((n * 100).toFixed(2))) / 100).toFixed(2);
}
